// src/pages/ops/ServiceBookingsTable.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTable, useSortBy, useFilters, usePagination } from "react-table";
import axios from "axios";
import { format } from "date-fns";
import Table from "../../components/tableComponent";
import ExternalBookingModal from "../../components/externalBookingModal.jsx";

const ExternalBookingsTable = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  /* — filters — */
  const [filterCol, setFilterCol] = useState("");
  const [filterInput, setFilterInput] = useState("");
  const [hideClosed, setHideClosed] = useState(true);

  /* ───────────────── columns ───────────────── */
  const columns = useMemo(
    () => [
      {
        Header: "Created",
        accessor: "createdAt",
        Cell: ({ value }) =>
          value ? format(new Date(value), "dd.MM.yyyy") : "—",
      },
      {
        Header: "Booking date",
        accessor: "bookingDate",
        Cell: ({ value }) =>
          value ? format(new Date(value), "dd.MM.yyyy") : "—",
      },
      { Header: "Booking ID", accessor: "externalBookingId" },
      { Header: "Service Type", accessor: "packageName" },
      {
        Header: "Bike",
        accessor: (r) => `${r.bikeBrand} ${r.bikeModel}`,
        id: "bikeType",
      },
      {
        Header: "Customer",
        accessor: (r) => `${r.firstName} ${r.lastName}`,
        id: "customerName",
      },
      { Header: "Phone", accessor: "phone" },
      {
        Header: "Delivery",
        accessor: "deliveryType", // nydalen / transport
        Cell: ({ value }) => (value === "transport" ? "Transport" : "Nydalen"),
      },
      { Header: "Status", accessor: "status" },
    ],
    []
  );

  /* ───────────────── data fetch ───────────────── */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("/api/external-bookings");
        setBookings(data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ───────────────── pre‑filter closed ───────────────── */
  const filteredData = useMemo(
    () =>
      hideClosed ? bookings.filter((b) => b.status !== "Closed") : bookings,
    [bookings, hideClosed]
  );

  /* ───────────────── react‑table hooks ───────────────── */
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    previousPage,
    nextPage,
    setFilter,
    rows,
    state: { pageIndex, pageSize },
    setPageSize,
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: {
        pageIndex: 0,
        pageSize: 50,
        sortBy: [{ id: "createdAt", desc: true }],
      },
    },
    useFilters,
    useSortBy,
    usePagination
  );

  /* ───────────────── status colour coding ───────────────── */
  const prepareRowColour = useCallback(
    (row) => {
      prepareRow(row);
      const s = row.original.status;
      let colour = null;
      if (s === "New" || s === "Received") colour = "bg-yellow-100";
      else if (s === "AwaitingCustomer") colour = "bg-red-100";
      else if (s === "ReadyForService") colour = "bg-green-100";
      else if (s === "Completed") colour = "bg-gray-100";

      if (colour) {
        const base = row.getRowProps();
        row.getRowProps = (extra = {}) => ({
          ...base,
          ...extra,
          className: [base.className, colour].join(" "),
        });
      }
    },
    [prepareRow]
  );

  /* ───────────────── filter change handler ───────────────── */
  const handleFilterInput = (e) => {
    setFilterInput(e.target.value);
    if (filterCol) setFilter(filterCol, e.target.value);
  };

  /* ───────────────── optimistic local update ───────────────── */
  const updateLocal = (updated) =>
    setBookings((prev) =>
      prev.map((b) => (b._id === updated._id ? updated : b))
    );

  if (loading) return <div>Loading…</div>;

  return (
    <div className="flex-grow p-4">
      <h2 className="text-2xl font-semibold mb-4">Service bookings</h2>

      {/* ① filter controls */}
      <div className="flex justify-between items-center mb-4">
        <select
          value={filterCol}
          onChange={(e) => setFilterCol(e.target.value)}
          className="border p-2 rounded mr-2"
        >
          <option value="">Select column…</option>
          {columns
            .filter((c) => typeof c.accessor === "string" || c.id)
            .map((c) => (
              <option key={c.id || c.accessor} value={c.id || c.accessor}>
                {c.Header}
              </option>
            ))}
        </select>

        <label className="flex items-center space-x-2 bg-gray-200 px-3 py-1 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={hideClosed}
            onChange={(e) => setHideClosed(e.target.checked)}
            className="form-checkbox text-black"
          />
          <span className="text-sm font-medium text-gray-700">Hide closed</span>
        </label>
      </div>

      <input
        value={filterInput}
        onChange={handleFilterInput}
        placeholder="Enter filter value…"
        className="border p-2 rounded w-full mb-4"
      />

      {/* ② table */}
      <Table
        columns={columns}
        data={page}
        onRowClick={(row) => setSelected(row.original)}
        getTableProps={getTableProps}
        getTableBodyProps={getTableBodyProps}
        headerGroups={headerGroups}
        prepareRow={prepareRowColour}
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        previousPage={previousPage}
        nextPage={nextPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        pageIndex={pageIndex}
        totalFilteredRows={rows.length}
      />

      {/* ③ modal */}
      {selected && (
        <ExternalBookingModal
          isOpen
          booking={selected}
          closeModal={() => setSelected(null)}
          onUpdated={updateLocal}
        />
      )}
    </div>
  );
};

export default ExternalBookingsTable;
